import { ComponentPropsWithoutRef, forwardRef } from 'react';

import { cn } from '../utils/cn';
import { makeInitials, uuidToColor } from './utils';

export type AvatarShape = 'circle' | 'square';

export type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export const sizeToContainerSize: Record<AvatarSize, number> = {
  xxs: 16,
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
  '2xl': 56,
  '3xl': 96,
};

export const sizeToFontSize: Record<AvatarSize, number> = {
  xxs: 10,
  xs: 10,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  '2xl': 24,
  '3xl': 32,
};

export type AvatarColor =
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'emerald'
  | 'cyan'
  | 'blue'
  | 'violet'
  | 'fuchsia'
  | 'rose'
  | 'gray';

export type AvatarTheme = 'dark' | 'light';

export const colorToThemeToClassName: Record<AvatarColor, Record<AvatarTheme, string>> = {
  orange: {
    dark: 'bg-orange-500',
    light: 'bg-orange-100',
  },
  yellow: {
    dark: 'bg-yellow-500',
    light: 'bg-yellow-100',
  },
  lime: {
    dark: 'bg-lime-500',
    light: 'bg-lime-100',
  },
  emerald: {
    dark: 'bg-emerald-500',
    light: 'bg-emerald-100',
  },
  cyan: {
    dark: 'bg-cyan-500',
    light: 'bg-cyan-100',
  },
  blue: {
    dark: 'bg-blue-500',
    light: 'bg-blue-100',
  },
  violet: {
    dark: 'bg-violet-500',
    light: 'bg-violet-100',
  },
  fuchsia: {
    dark: 'bg-fuchsia-500',
    light: 'bg-fuchsia-100',
  },
  rose: {
    dark: 'bg-rose-500',
    light: 'bg-rose-100',
  },
  gray: {
    dark: 'bg-slate-50',
    light: 'bg-slate-50',
  },
};

export interface AvatarProps extends ComponentPropsWithoutRef<'div'> {
  shape?: AvatarShape;
  size?: AvatarSize;
  color?: AvatarColor;
  theme?: AvatarTheme;
  uuid?: string; // allows to generate a color based on the uuid
}

/**
 * Avatars represent users and organizations.
 * @see {@link https://stylus.design/avatar}
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      shape = 'circle',
      size = 'md',
      color = 'blue',
      theme = 'dark',
      uuid,
      style,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const containerSize = sizeToContainerSize[size];
    const selectedColor = uuid ? uuidToColor(uuid).color : color;
    const showInitials = typeof children === 'string';

    return (
      <div
        ref={ref}
        style={{
          width: containerSize,
          height: containerSize,
          fontSize: sizeToFontSize[size],
          ...(theme === 'dark'
            ? {
                textShadow: '0px 1px 1px rgba(15, 23, 42, 0.25)',
              }
            : {}),
          ...(size === '3xl' && theme === 'dark'
            ? {
                textShadow: '0px 2px 4px rgba(15, 23, 42, 0.25)',
              }
            : {}),
          ...style,
        }}
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden font-sans font-bold uppercase leading-none',
          colorToThemeToClassName[selectedColor]?.[theme],
          {
            [colorToThemeToClassName[selectedColor]?.[theme]]: showInitials,
            'bg-slate-100': !showInitials,
            'text-white': theme === 'dark',
            'text-slate-900': theme !== 'dark',
            'font-bold': size !== '3xl',
            'font-extrabold': size === '3xl',
          },
          shape === 'circle' ? 'rounded-full' : 'rounded',
          className,
        )}
        {...props}
      >
        {showInitials ? makeInitials(children, 1) : children}
      </div>
    );
  },
);
